"use client";

import { CircleAlert } from "lucide-react";
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";
import { Wordmark } from "../brand/logo";
import { Breadcrumb } from "../components/breadcrumb";
import { Icon } from "../components/icon";
import { Input, Label, Select, Textarea } from "../components/input";
import { Timeline, type TimelineStep } from "../components/timeline";
import { cn } from "../lib/cn";
import { useText } from "./text";

/** What describes a field's control: its error when there is one, its hint otherwise. */
const describedBy = (id: string, hint: string | undefined, error: string | undefined) =>
  error !== undefined ? `${id}-error` : hint !== undefined ? `${id}-hint` : undefined;

/**
 * A field's frame (docs/design/meridian.md, Field): the label above, then the control, then the hint in caption. An
 * error replaces the hint, in `problem` text with an icon.
 */
export function FieldFrame({
  id,
  label,
  hint,
  error,
  className,
  children,
}: {
  id: string;
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  className?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error !== undefined ? (
        <p id={`${id}-error`} className="flex items-start gap-1.5 text-caption text-tone-problem-fg">
          <Icon icon={CircleAlert} size={16} className="mt-px size-3.5 shrink-0" />
          {error}
        </p>
      ) : (
        hint !== undefined && (
          <p id={`${id}-hint`} className="text-caption text-ink-muted">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

/**
 * A labelled input with an optional hint and error, all text already translated. `unit` sits inside the control on
 * the right ("tonnes").
 */
export function Field({
  id,
  label,
  hint,
  error,
  unit,
  className,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  unit?: string | undefined;
}) {
  const control = {
    id,
    "aria-invalid": error !== undefined,
    "aria-describedby": describedBy(id, hint, error),
    ...input,
  };
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} className={className}>
      {unit === undefined ? (
        <Input {...control} />
      ) : (
        <div className="flex">
          <Input
            {...control}
            className="rounded-e-none tabular-nums focus-visible:relative focus-visible:z-10"
          />
          <span className="inline-flex h-9 shrink-0 items-center rounded-e-(--radius-control) border border-s-0 border-border-strong bg-canvas px-3 text-small text-ink-muted">
            {unit}
          </span>
        </div>
      )}
    </FieldFrame>
  );
}

/** A labelled select of already-translated options. */
export function SelectField({
  id,
  label,
  value,
  options,
  onChange,
  hint,
  error,
  className,
}: {
  id: string;
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
  hint?: string | undefined;
  error?: string | undefined;
  className?: string | undefined;
}) {
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} className={className}>
      <Select
        id={id}
        value={value}
        aria-invalid={error !== undefined}
        aria-describedby={describedBy(id, hint, error)}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </FieldFrame>
  );
}

/** A labelled text area with an optional hint and error, as `Field` is for one line. */
export function TextareaField({
  id,
  label,
  hint,
  error,
  className,
  ...area
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  id: string;
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
}) {
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} className={className}>
      <Textarea
        id={id}
        aria-invalid={error !== undefined}
        aria-describedby={describedBy(id, hint, error)}
        {...area}
      />
    </FieldFrame>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="text-small text-tone-problem-fg">
      {children}
    </p>
  );
}

/**
 * A centred column for the sign-in and onboarding steps (cards 108 and 113): the Civentia wordmark at 20 px, during
 * onboarding its steps in a row, then the step's card. `wide` gives a form of two columns its room.
 */
export function StepPanel({
  title,
  subtitle,
  steps,
  wide = false,
  children,
}: {
  title: string;
  subtitle?: string | undefined;
  steps?: readonly TimelineStep[] | undefined;
  wide?: boolean | undefined;
  children: ReactNode;
}) {
  const { t } = useText();
  return (
    <main className="flex min-h-screen items-start justify-center bg-canvas px-4 py-10 sm:items-center sm:px-6">
      <div className={cn("flex w-full flex-col items-center gap-6", wide ? "max-w-2xl" : "max-w-md")}>
        <Wordmark label={t("common.productName")} height={20} className="text-ink" />
        {steps !== undefined && (
          <Timeline
            steps={steps}
            orientation="horizontal"
            label={t("onboarding.steps.label")}
            className="w-full max-w-md px-1"
          />
        )}
        <section className="flex w-full flex-col gap-5 rounded-(--radius-card) border border-border bg-surface p-6 shadow-raised sm:p-8">
          <header className="flex flex-col gap-1.5">
            <h1 className="text-title font-semibold text-ink">{title}</h1>
            {subtitle !== undefined && <p className="text-body text-ink-muted">{subtitle}</p>}
          </header>
          {children}
        </section>
      </div>
    </main>
  );
}

/** Keeps only digits, at most six, for one-time and TOTP codes. */
export function codeValue(value: string): string {
  return value.replace(/\D/g, "").slice(0, 6);
}

/** The way back from a page to its list, as a breadcrumb: the list, then the page (cards 112 and 113). */
export function BackCrumb({ back, current, onBack }: { back: string; current: string; onBack: () => void }) {
  const { t } = useText();
  return (
    <Breadcrumb
      label={t("common.breadcrumb")}
      items={[{ label: back, href: "#" }, { label: current }]}
      renderLink={(_, children, className) => (
        <button type="button" className={className} onClick={onBack}>
          {children}
        </button>
      )}
    />
  );
}
